import { getTenantBasePath, resolveTenantFromUrl } from './tenant-resolver';

/** Canonical lobby path: /tenant/{tenant}/lobby/{lobbySlug} */
export function getLobbyHomePath(): string {
  const tenantPath = getTenantBasePath();
  const tenant = resolveTenantFromUrl();
  return `${tenantPath}/lobby/${tenant}`;
}

/** Full lobby home URL for a given origin (e.g. https://freetoplay-prod.low6.com/tenant/dk/lobby/dk). */
export function buildLobbyHomeUrl(origin: string): string {
  const normalized = origin.replace(/\/$/, '');
  return `${normalized}${getLobbyHomePath()}`;
}

const LOBBY_RETURN_PARAM = 'lobbyReturn';

/**
 * Lobby in-app view suffixes (e.g. `/mygames`) that are deep-links inside the
 * lobby SPA. "Back to Lobby" must return to the main lobby home, not reopen
 * that nested view — otherwise completed-contest back navigation can land on
 * `/lobby/{slug}/mygames` and appear stuck / do nothing useful.
 */
const LOBBY_VIEW_SUFFIXES = ['mygames'] as const;

/**
 * Strip lobby in-app view path segments so the return URL is the main lobby.
 *
 * Examples:
 * - `/tenant/dk/lobby/default/mygames` → `/tenant/dk/lobby/default`
 * - `/tenant/dk/mygames` → `/tenant/dk`
 * Query string / hash are preserved.
 */
export function toLobbyHomeReturnUrl(href: string): string {
  try {
    const url = new URL(href);
    let path = url.pathname.replace(/\/+$/, '') || '/';

    for (const suffix of LOBBY_VIEW_SUFFIXES) {
      const token = `/${suffix}`;
      if (path === token) {
        path = '/';
        break;
      }
      if (path.endsWith(token)) {
        path = path.slice(0, -token.length) || '/';
        break;
      }
    }

    url.pathname = path;
    return url.toString();
  } catch {
    return href;
  }
}

function decodeLobbyReturnParam(raw: string): string {
  try {
    // URLSearchParams.get already decodes once; only decode again when still encoded.
    return /%([0-9A-Fa-f]{2})/.test(raw) ? decodeURIComponent(raw) : raw;
  } catch {
    return raw;
  }
}

/**
 * Resolve where "Back to Lobby" should navigate.
 *
 * Priority:
 * 1. `lobbyReturn` query param (full URL set by the lobby when launching a game)
 * 2. sessionStorage value captured on first land
 * 3. document.referrer when it points at a /lobby/ route
 * 4. Default lobby home URL using referrer origin or current origin
 *
 * Always canonicalizes to the main lobby (strips `/mygames` and future view suffixes).
 */
export function resolveLobbyReturnUrl(
  searchParams: URLSearchParams,
  sessionStorageKey: string
): string {
  return safeLobbyReturnUrl(resolveUncheckedLobbyReturnUrl(searchParams, sessionStorageKey));
}

/**
 * Hosts a "Back to Lobby" navigation may leave for. The return URL arrives in a query parameter
 * and is assigned to `location.href`, so anything else is an open redirect or, with a
 * `javascript:` URL, script on this origin.
 */
const TRUSTED_LOBBY_DOMAINS = ['low6.com', 'draftkings.com', 'azurestaticapps.net'] as const;

export function isSafeLobbyReturnUrl(href: string): boolean {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return false;
  }
  const isLocal = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
  if (url.protocol !== 'https:' && !(isLocal && url.protocol === 'http:')) return false;
  if (isLocal || url.origin === window.location.origin) return true;
  return TRUSTED_LOBBY_DOMAINS.some(
    domain => url.hostname === domain || url.hostname.endsWith(`.${domain}`)
  );
}

/** The candidate when it is a safe lobby URL, otherwise the default lobby on this origin. */
export function safeLobbyReturnUrl(candidate: string | null | undefined): string {
  return candidate && isSafeLobbyReturnUrl(candidate)
    ? candidate
    : buildLobbyHomeUrl(window.location.origin);
}

function resolveUncheckedLobbyReturnUrl(
  searchParams: URLSearchParams,
  sessionStorageKey: string
): string {
  const fromParam = searchParams.get(LOBBY_RETURN_PARAM);
  if (fromParam) {
    return toLobbyHomeReturnUrl(decodeLobbyReturnParam(fromParam));
  }

  const stored = sessionStorage.getItem(sessionStorageKey);
  if (stored) return toLobbyHomeReturnUrl(stored);

  const referrer = document.referrer;
  if (referrer) {
    try {
      const url = new URL(referrer);
      if (url.pathname.includes('/lobby/') || /\/mygames\/?$/.test(url.pathname)) {
        return toLobbyHomeReturnUrl(referrer);
      }
      if (url.origin !== window.location.origin) {
        return buildLobbyHomeUrl(url.origin);
      }
    } catch {
      // ignore malformed referrer
    }
  }

  return buildLobbyHomeUrl(window.location.origin);
}

/**
 * Append lobby return URL to a game play link (used by the lobby app).
 * Uses the main lobby home even when the user launched from My Games (`/mygames`).
 */
export function appendLobbyReturnToPlayUrl(playUrl: string): string {
  const sep = playUrl.includes('?') ? '&' : '?';
  const lobbyReturn = encodeURIComponent(toLobbyHomeReturnUrl(window.location.href));
  return `${playUrl}${sep}from=lobby&lobbyReturn=${lobbyReturn}`;
}
